/**
 * InvitationDashboard — View and manage Aegix One invitations.
 *
 * Shows list of sent invitations with status, acceptance stats,
 * and resend/revoke actions. Used within the SMS Reunification page.
 */

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { toaster } from '../ui/toaster';
import {
  listInvitations,
  resendInvitation,
  revokeInvitation,
  getInvitationStats,
  type Invitation,
  type InvitationStats,
} from '../../services/oneInvitations.service';

interface InvitationDashboardProps {
  schoolId?: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  accepted: 'green',
  expired: 'gray',
  revoked: 'red',
};

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  both: 'Email & SMS',
};

export function InvitationDashboard({ schoolId }: InvitationDashboardProps) {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [stats, setStats] = useState<InvitationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [inviteResult, statsResult] = await Promise.all([
        listInvitations({ school_id: schoolId }),
        getInvitationStats(schoolId),
      ]);
      setInvitations(inviteResult.results);
      setStats(statsResult);
    } catch {
      toaster.error({ title: t('invitations.failedLoad', 'Failed to load invitations') });
    } finally {
      setLoading(false);
    }
  }, [schoolId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResend = async (publicId: string) => {
    setActionLoading(publicId);
    try {
      await resendInvitation(publicId);
      toaster.success({ title: t('invitations.resent', 'Invitation resent') });
      await fetchData();
    } catch {
      toaster.error({ title: t('invitations.failedResend', 'Failed to resend') });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (publicId: string) => {
    setActionLoading(publicId);
    try {
      await revokeInvitation(publicId);
      toaster.success({ title: t('invitations.revoked', 'Invitation revoked') });
      await fetchData();
    } catch {
      toaster.error({ title: t('invitations.failedRevoke', 'Failed to revoke') });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  return (
    <Box px={5} py={4}>
      <VStack gap={5} align="stretch">
        {/* Stats summary */}
        {stats && (
          <HStack gap={4} flexWrap="wrap">
            <StatBox label={t('common.all', 'Total')} value={stats.total_invitations} data-e2e="stat-total-invitations" />
            <StatBox
              label={t('common.pending', 'Pending')}
              value={stats.pending}
              color="yellow.500"
              data-e2e="stat-pending-invitations"
            />
            <StatBox
              label={t('invitations.accepted', 'Accepted')}
              value={stats.accepted}
              color="green.500"
              data-e2e="stat-accepted-invitations"
            />
            <StatBox
              label={t('invitations.acceptanceRate', 'Acceptance Rate')}
              value={`${Math.round(stats.acceptance_rate * 100)}%`}
              color="blue.500"
              data-e2e="stat-acceptance-rate"
            />
          </HStack>
        )}

        {/* Invitation list */}
        {invitations.length === 0 ? (
          <Center py={8}>
            <Text color="text.secondary" fontSize="sm">
              {t('invitations.noInvitations', 'No invitations sent yet. Go to the Students tab and use the "Invite to ONE" button to get started.')}
            </Text>
          </Center>
        ) : (
          <VStack gap={2} align="stretch">
            {invitations.map((invite) => (
              <HStack
                key={invite.public_id}
                p={3}
                borderWidth="1px"
                borderColor="border.primary"
                borderRadius="md"
                justify="space-between"
                flexWrap="wrap"
                gap={2}
                data-e2e="invitation-item"
              >
                <Box flex="1" minW="200px">
                  <HStack gap={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      {invite.recipient_name ||
                        invite.recipient_email ||
                        invite.recipient_phone}
                    </Text>
                    <Badge
                      colorPalette={STATUS_COLORS[invite.status] || 'gray'}
                      size="sm"
                    >
                      {t(`common.${invite.status}`, invite.status)}
                    </Badge>
                    <Badge size="sm" variant="outline">
                      {CHANNEL_LABELS[invite.sent_via] || invite.sent_via}
                    </Badge>
                  </HStack>
                  <HStack gap={3} mt={0.5}>
                    {invite.recipient_email && (
                      <Text fontSize="xs" color="text.secondary">
                        {invite.recipient_email}
                      </Text>
                    )}
                    {invite.recipient_phone && (
                      <Text fontSize="xs" color="text.secondary">
                        {invite.recipient_phone}
                      </Text>
                    )}
                  </HStack>
                  <Text fontSize="xs" color="text.tertiary">
                    {invite.students
                      .map((s) => `${s.first_name} ${s.last_name}`)
                      .join(', ')}
                  </Text>
                </Box>

                {invite.status === 'pending' && (
                  <HStack gap={1}>
                    <Button
                      visual="secondary"
                      size="sm"
                      onClick={() => handleResend(invite.public_id)}
                      disabled={actionLoading === invite.public_id}
                      data-e2e="invitation-resend-button"
                    >
                      {t('invitations.resend', 'Resend')}
                    </Button>
                    <Button
                      visual="secondary"
                      size="sm"
                      onClick={() => handleRevoke(invite.public_id)}
                      disabled={actionLoading === invite.public_id}
                      data-e2e="invitation-revoke-button"
                    >
                      {t('invitations.revoke', 'Revoke')}
                    </Button>
                  </HStack>
                )}

                {invite.status === 'accepted' && invite.accepted_at && (
                  <Text fontSize="xs" color="text.secondary">
                    {t('invitations.acceptedAt', 'Accepted {{date}}', { date: new Date(invite.accepted_at).toLocaleDateString() })}
                  </Text>
                )}
              </HStack>
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

function StatBox({
  label,
  value,
  color,
  'data-e2e': dataE2e,
}: {
  label: string;
  value: number | string;
  color?: string;
  'data-e2e'?: string;
}) {
  return (
    <Box
      p={3}
      borderWidth="1px"
      borderColor="border.primary"
      borderRadius="md"
      minW="100px"
      textAlign="center"
      data-e2e={dataE2e}
    >
      <Text fontSize="2xl" fontWeight="bold" color={color || 'text.primary'}>
        {value}
      </Text>
      <Text fontSize="xs" color="text.secondary">
        {label}
      </Text>
    </Box>
  );
}
